import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DayPicker } from 'react-day-picker';
import { format, isSameDay, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Scale, MapPin, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { mockCourtDates, type CourtDate } from './mockData';

interface CourtCalendarProps {
  onDateSelect?: (date: Date, courtDates: CourtDate[]) => void;
}

export function CourtCalendar({ onDateSelect }: CourtCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Get dates that have court appointments
  const courtDateDays = useMemo(() => {
    return mockCourtDates.map((cd) => cd.date);
  }, []);

  // Get court dates for a specific day
  const getCourtDatesForDay = (date: Date): CourtDate[] => {
    return mockCourtDates.filter((cd) => isSameDay(cd.date, date));
  };

  // Get court dates for selected day
  const selectedDayCourtDates = selectedDate
    ? getCourtDatesForDay(selectedDate)
    : [];

  // Custom day render to show dots for court dates
  const renderDay = (date: Date) => {
    const courtDatesOnDay = getCourtDatesForDay(date);
    const hasCourtDate = courtDatesOnDay.length > 0;
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isCurrentMonth = isSameMonth(date, currentMonth);

    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span
          className={cn(
            !isCurrentMonth && 'text-slate-300',
            isSelected && 'font-semibold'
          )}
        >
          {date.getDate()}
        </span>
        {hasCourtDate && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
            {courtDatesOnDay.slice(0, 3).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  isSelected ? 'bg-white' : 'bg-purple-500'
                )}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleDayClick = (date: Date | undefined) => {
    if (!date) return;
    setSelectedDate(date);
    const courtDates = getCourtDatesForDay(date);
    onDateSelect?.(date, courtDates);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.2 }}
      className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden w-fit"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-purple-600" />
          <h2 className="font-semibold text-slate-900">Gerichtskalender</h2>
        </div>
      </div>

      {/* Calendar */}
      <div className="p-4">
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={handleDayClick}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          locale={de}
          showOutsideDays
          className="!m-0"
          classNames={{
            months: 'flex flex-col',
            month: 'space-y-3',
            month_caption: 'flex justify-center pt-1 relative items-center mb-2',
            caption_label: 'text-sm font-semibold text-slate-900',
            nav: 'space-x-1 flex items-center',
            button_previous: cn(
              'absolute left-0 h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100',
              'inline-flex items-center justify-center rounded-md',
              'text-slate-600 hover:bg-slate-100 transition-colors'
            ),
            button_next: cn(
              'absolute right-0 h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100',
              'inline-flex items-center justify-center rounded-md',
              'text-slate-600 hover:bg-slate-100 transition-colors'
            ),
            weekdays: 'flex w-full',
            weekday: 'text-slate-500 rounded-md w-9 font-medium text-[0.7rem] uppercase text-center',
            week: 'flex w-full mt-1',
            day: cn(
              'h-9 w-9 text-center text-sm p-0 relative',
              'focus-within:relative focus-within:z-20'
            ),
            day_button: cn(
              'h-9 w-9 p-0 font-normal',
              'inline-flex items-center justify-center rounded-md text-sm',
              'transition-colors hover:bg-slate-100',
              'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1'
            ),
            selected: cn(
              'bg-purple-600 text-white hover:bg-purple-700',
              'focus:bg-purple-700'
            ),
            today: 'bg-slate-100 font-semibold',
            outside: 'text-slate-300 opacity-50',
            disabled: 'text-slate-300',
          }}
          components={{
            Chevron: ({ orientation }) =>
              orientation === 'left' ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              ),
            DayButton: ({ day, modifiers, ...props }) => (
              <button {...props}>{renderDay(day.date)}</button>
            ),
          }}
        />
      </div>

      {/* Selected Day Details */}
      <AnimatePresence mode="wait">
        {selectedDate && selectedDayCourtDates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-slate-100"
          >
            <div className="p-4 space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                {format(selectedDate, "EEEE, d. MMMM", { locale: de })}
              </p>
              {selectedDayCourtDates.map((courtDate, index) => (
                <motion.div
                  key={courtDate.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-purple-50 rounded-lg p-3 border border-purple-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-purple-100 rounded">
                      <Scale className="h-3.5 w-3.5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm">
                        {courtDate.caseTitle}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {courtDate.caseRef}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(courtDate.date, 'HH:mm')} Uhr
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {courtDate.room}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {courtDate.courtName}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="w-2 h-2 rounded-full bg-purple-500" />
          <span>Gerichtstermin geplant</span>
        </div>
      </div>
    </motion.div>
  );
}
